/**
 * Runs a reader job and follows it to completion.
 *
 * Both surfaces (the map card and the ticket panel) need exactly the same
 * behaviour — start something, poll until it stops, keep the last result on
 * screen — so it lives here once rather than being written twice and drifting.
 *
 * Polling stops as soon as the job reaches a terminal state, and on unmount,
 * so a force scan left open in a background tab doesn't keep hitting the API.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  JOB_POLL_MS,
  type ReaderJob,
  fetchReaderJob,
  startForceScan,
  startTestScan,
} from "../services/readerApi";

interface UseReaderJob {
  job: ReaderJob | null;
  busy: boolean;
  error: string | null;
  /** `readerId` picks which reader to drive on a multi-reader box; omitted,
   *  the server uses the lowest-numbered active one. */
  runTestScan: (antennas?: number[], readerId?: number) => Promise<void>;
  runForceScan: () => Promise<void>;
  clearError: () => void;
}

export function useReaderJob(
  token: string | null,
  scu: string,
  initialJob: ReaderJob | null = null,
): UseReaderJob {
  const [job, setJob] = useState<ReaderJob | null>(initialJob);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards the async poll loop against setting state after unmount.
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Adopt a job supplied later (the status fetch resolves after first render).
  useEffect(() => {
    if (initialJob && !job) setJob(initialJob);
  }, [initialJob?.job_id]);

  // Follow whatever job is current until it stops running.
  useEffect(() => {
    if (!token || !job || !job.is_running) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async (): Promise<void> => {
      try {
        const next = await fetchReaderJob(token, scu, job.job_id);
        if (!alive.current) return;
        setJob(next);
        if (next.is_running) timer = setTimeout(() => void tick(), JOB_POLL_MS);
      } catch (e) {
        if (!alive.current) return;
        // A transient poll failure shouldn't kill the watch — the job is still
        // running on the box regardless of one bad request.
        setError(e instanceof Error ? e.message : String(e));
        timer = setTimeout(() => void tick(), JOB_POLL_MS * 2);
      }
    };
    timer = setTimeout(() => void tick(), JOB_POLL_MS);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [token, scu, job]);

  const start = useCallback(
    async (fn: () => Promise<ReaderJob>): Promise<void> => {
      if (!token) return;
      setBusy(true);
      setError(null);
      try {
        const started = await fn();
        if (alive.current) setJob(started);
      } catch (e) {
        if (alive.current) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [token],
  );

  const runTestScan = useCallback(
    (antennas?: number[], readerId?: number) =>
      start(() => startTestScan(token as string, scu, antennas, readerId)),
    [start, token, scu],
  );

  const runForceScan = useCallback(
    () => start(() => startForceScan(token as string, scu)),
    [start, token, scu],
  );

  return {
    job,
    busy,
    error,
    runTestScan,
    runForceScan,
    clearError: () => setError(null),
  };
}
